import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableHead, TableRow, TableCell, TableBody, Chip } from '@mui/material';
import { Color } from '../../../assets/color';
import { DeleteSvg, EditSvg } from '../../../assets/svg';
import RouteHeader from '../../../components/common/RouteHeader';
import { HideDateFromCurrent } from '../../../utils/common-function';
import DatatableHeading from '../../../components/heading/DatatableHeading';
import DatatableWithSearch from '../../../components/datatable/DatatableWithSearch';
import * as SuperadminActions from '../../../redux/actions/superadminAction';

const DiscountCoupons = () => {
    const [mode, setMode] = useState('');
    const [usageModalOpen, setUsageModalOpen] = useState(false);
    const [selectedCoupon, setSelectedCoupon] = useState(null);
    const dispatch = useDispatch();
    const { couponData } = useSelector(state => state?.superadminReducer);

    const [inputFieldDetail, setInputFieldDetail] = useState({ coupon_code: '', discount_percentage: '', start_date: '', end_date: '', coupon_type: '' });
    const [inputFieldError, setInputFieldError] = useState({});

    const handleInputFieldDetail = (event) => setInputFieldDetail({ ...inputFieldDetail, [event?.target?.name]: event?.target?.value });
    const handleInputFieldDetailError = (input, value) => setInputFieldError((prev) => ({ ...prev, [input]: value }));

    const handleReset = () => {
        setInputFieldDetail({ coupon_code: '', discount_percentage: '', start_date: '', end_date: '', coupon_type: '' });
        setMode('');
    };

    const handleEdit = (data) => {
        setInputFieldDetail({ ...data, start_date: moment.utc(data?.start_date).format('YYYY-MM-DDTHH:mm'), end_date: moment.utc(data?.end_date).format('YYYY-MM-DDTHH:mm'), id: data?._id });
        setMode('Edit');
    };

    const handleSubmit = () => {
        const { coupon_code, discount_percentage, start_date, end_date, coupon_type } = inputFieldDetail;

        if (mode == 'Edit') {
            const payload = {
                id: inputFieldDetail?.id,
                data: { coupon_code, discount_percentage, start_date, end_date, coupon_type },
                onComplete: () => handleReset()
            }
            dispatch(SuperadminActions.updateCoupon(payload));
        } else {
            const payload = {
                data: { coupon_code, discount_percentage, start_date, end_date, coupon_type },
                onComplete: () => handleReset()
            }
            dispatch(SuperadminActions.createCoupon(payload));
        }
    };

    const isExpired = (row) => moment.utc(row?.end_date).isBefore(moment());

    const handleViewUsage = (row) => {
        setSelectedCoupon(row);
        setUsageModalOpen(true);
    };

    const columns = [
        { name: 'S.No.', selector: row => couponData?.indexOf(row) + 1, width: '70px' },
        {
            name: 'Coupon',
            cell: row => (
                <div style={{ textTransform: 'capitalize', opacity: isExpired(row) ? 0.5 : 1 }}>
                    {row?.coupon_code}
                </div>
            ),
            width: '180px'
        },
        { name: 'Discount(%)', selector: row => row?.discount_percentage, width: '100px' },
        { name: 'Start Date', selector: row => moment.utc(row?.start_date).format('DD-MMM-YYYY @ HH:mm A'), width: '220px' },
        { name: 'End Date', selector: row => moment.utc(row?.end_date).format('DD-MMM-YYYY @ HH:mm A'), width: '220px' },
        { name: 'Coupon Type', selector: row => row?.coupon_type?.replace(/_/g, ' '), width: '170px' },
        {
            name: 'Status',
            cell: row => (
                <Chip
                    label={isExpired(row) ? 'Expired' : 'Active'}
                    size="small"
                    sx={{
                        backgroundColor: isExpired(row) ? '#fce8e6' : '#e6f4ea',
                        color: isExpired(row) ? '#c62828' : '#1e7e34',
                        fontWeight: 600, fontSize: '11px'
                    }}
                />
            ),
            width: '100px'
        },
        { name: 'Used By', selector: row => row?.used_by?.length || 0, width: '80px' },
        {
            name: 'Action',
            cell: row => {
                const expired = isExpired(row);
                return (
                    <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                        {expired ? (
                            <div
                                onClick={() => handleViewUsage(row)}
                                style={{ cursor: "pointer", fontSize: '12px', padding: '4px 10px', borderRadius: '12px', backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600 }}
                            >
                                View Usage
                            </div>
                        ) : (
                            <>
                                <div onClick={() => handleEdit(row)} style={{ cursor: "pointer" }}><EditSvg /></div>
                                <div onClick={() => dispatch(SuperadminActions?.deleteCoupon({ id: row?._id }))} style={{ cursor: "pointer" }}><DeleteSvg /></div>
                            </>
                        )}
                    </div>
                );
            },
            right: 'true',
            width: '140px'
        },
    ];

    useEffect(() => {
        dispatch(SuperadminActions.getCoupons());
    }, []);

    return (
        <>
            <RouteHeader title={`Discount Coupons`} sub_title={`Create and manage discount coupons for your reports.`} />

            <div style={{ backgroundColor: Color.white, padding: "20px", borderRadius: "10px" }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <DatatableHeading title={'Coupon Management'} sub_title={'Manage discount coupons for your reports. Only Super Admins can create, edit, or delete coupons.'} />
                    <div onClick={() => setMode('Add')} style={{ fontSize: '14.5px', fontWeight: '500', padding: '10px 20px 9px 20px', borderRadius: '20px', background: "linear-gradient(to right,  #6653E8, #9544EB)", color: "#FFF", cursor: 'pointer' }}>{mode ? mode : 'Add'} Coupon</div>
                </div>

                <Grid container spacing={3} sx={{ marginTop: '20px' }}>
                    {mode && <>
                        <Grid item lg={2.5} md={12} sm={12} xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Coupon type</InputLabel>
                                <Select
                                    label="Coupon type" variant="outlined" fullWidth
                                    name='coupon_type'
                                    value={inputFieldDetail?.coupon_type}
                                    onChange={handleInputFieldDetail}
                                    error={inputFieldError?.coupon_type ? true : false}
                                    onFocus={() => handleInputFieldDetailError("coupon_type", null)}
                                >
                                    <MenuItem disabled>---Select coupon_type ---</MenuItem>
                                    <MenuItem value="single_global_use">Single global use</MenuItem>
                                    <MenuItem value="multi_user_single_use">Multi user single use</MenuItem>
                                    <MenuItem value="multi_user_multiple_use">Multi user multiple use</MenuItem>
                                </Select>
                            </FormControl>
                            {inputFieldError?.coupon_type && <div style={{ color: "#D32F2F", fontSize: "13px", padding: "5px 15px 0 12px", fontWeight: "500" }}>{inputFieldError?.coupon_type}</div>}
                        </Grid>

                        <Grid item lg={2.5} sm={12} md={12} xs={12}>
                            <TextField label="Coupon" variant="outlined" fullWidth
                                name="coupon_code"
                                value={inputFieldDetail?.coupon_code}
                                onChange={handleInputFieldDetail}
                                error={inputFieldError.coupon_code ? true : false}
                                helperText={inputFieldError.coupon_code}
                                onFocus={() => handleInputFieldDetailError("coupon_code", null)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item lg={2} sm={12} md={12} xs={12}>
                            <TextField label="Discount (%)" variant="outlined" fullWidth type='number'
                                name="discount_percentage"
                                value={inputFieldDetail?.discount_percentage}
                                onChange={handleInputFieldDetail}
                                error={inputFieldError.discount_percentage ? true : false}
                                helperText={inputFieldError.discount_percentage}
                                onFocus={() => handleInputFieldDetailError("discount_percentage", null)}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item lg={2.5} sm={12} md={12} xs={12}>
                            <TextField label="Start date" variant="outlined" fullWidth type='datetime-local'
                                name="start_date"
                                value={inputFieldDetail?.start_date}
                                onChange={handleInputFieldDetail}
                                error={inputFieldError.start_date ? true : false}
                                helperText={inputFieldError.start_date}
                                onFocus={() => handleInputFieldDetailError("start_date", null)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: HideDateFromCurrent() }}
                            />
                        </Grid>

                        <Grid item lg={2.5} sm={12} md={12} xs={12}>
                            <TextField label="End date" variant="outlined" fullWidth type='datetime-local'
                                name="end_date"
                                value={inputFieldDetail?.end_date}
                                onChange={handleInputFieldDetail}
                                error={inputFieldError.end_date ? true : false}
                                helperText={inputFieldError.end_date}
                                onFocus={() => handleInputFieldDetailError("end_date", null)}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ min: HideDateFromCurrent() }}
                            />
                        </Grid>

                        <Grid item lg={12} md={12} sm={12} xs={12}>
                            <Grid container spacing={2} justifyContent="flex-end">
                                <Grid item>
                                    <div onClick={() => setMode('')} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', backgroundColor: Color.white, color: Color.primary, fontSize: '14px', cursor: 'pointer' }}>Cancel</div>
                                </Grid>

                                <Grid item>
                                    <div onClick={handleSubmit} style={{ padding: '10px 20px', border: `1px solid ${Color.primary}`, borderRadius: '5px', background: "linear-gradient(to right,  #6653E8, #9544EB)", color: Color.white, fontSize: '14px', cursor: 'pointer' }}>{mode} Coupon</div>
                                </Grid>
                            </Grid>
                        </Grid>
                    </>}

                    <Grid item lg={12} md={12} sm={12} xs={12}>
                        <DatatableWithSearch data={couponData} columns={columns} />
                    </Grid>
                </Grid>
            </div>

            {/* Usage Report Modal */}
            <Dialog open={usageModalOpen} onClose={() => setUsageModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 600 }}>
                    Coupon Usage Report — <span style={{ textTransform: 'uppercase', color: Color.primary }}>{selectedCoupon?.coupon_code}</span>
                </DialogTitle>
                <DialogContent>
                    <div style={{ marginBottom: '10px', fontSize: '13px', color: '#666' }}>
                        Discount: {selectedCoupon?.discount_percentage}% | Type: {selectedCoupon?.coupon_type?.replace(/_/g, ' ')} | Total Users: {selectedCoupon?.used_by?.length || 0}
                    </div>
                    {selectedCoupon?.used_by?.length > 0 ? (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>S.No.</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>User ID</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>Times Used</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedCoupon?.used_by?.map((user, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{user?.userId || '—'}</TableCell>
                                        <TableCell>{user?.usedCount || 1}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No users have used this coupon yet.</div>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUsageModalOpen(false)} variant="outlined">Close</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default DiscountCoupons;